// @ts-nocheck
import {
  Box,
  Button,
  Grid,
  Modal,
  Typography,
  Checkbox,
  useMediaQuery,
  IconButton,
  alpha,
} from "@mui/material";
import { titleSx } from "./AddClientModal";
import PropTypes from "prop-types";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";
import {
  CLIENT_EMAIL,
  CLIENT_EMAIL_LABEL,
} from "../../../utils/constants/client";
import { phoneNumberFormat } from "../../../utils/methods/formatters";
import { useEffect, useState } from "react";
import CompaniesAutocomplete from "../../../components/Custom/CompaniesAutocompelte";
import { CustomChip } from "../../../components/Custom/CustomIconButton";
import { CustomTooltip } from "../../../components/Tooltip/ClientTool";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { keyframes } from "@mui/material";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  boxShadow: 24,
  p: 4,
  minWidth: "800px",
  borderRadius: "16px",
  padding: "20px",
  border: "2px solid #D175B6",
  backgroundColor: "#000",
  zIndex: 9,
  maxHeight: "95vh",
  overflowY: "auto",

  "@media (max-width: 600px)": {
    minWidth: "0px",
    width: "97vw",
    maxHeight: "95vh",
    overflowY: "auto",
  },
};

const DataSection = ({
  title,
  data,
  source,
  onAccept,
  onReject,
  acceptanceState,
  isEmailSection = false,
  hasData = true,
}) => {
  return (
    <Box
      sx={{
        border: `1px solid ${source === "provided" ? "#6d6c6c" : "#D175B6"}`,
        borderRadius: "0.5rem",
        p: 1,
        px: 2,
        height: "100%",
        backgroundColor:
          acceptanceState === "accepted"
            ? "rgba(76, 175, 80, 0.1)"
            : acceptanceState === "rejected"
              ? "rgba(244, 67, 54, 0.05)"
              : "transparent",
        opacity: acceptanceState === "rejected" ? 0.85 : 1,
        transform: acceptanceState === "accepted" ? "scale(1.02)" : "scale(1)",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          acceptanceState === "accepted"
            ? "0 0 20px rgba(76, 175, 80, 0.3)"
            : acceptanceState === "rejected"
              ? "0 0 10px rgba(244, 67, 54, 0.2)"
              : "none",
        "&:hover": {
          transform:
            acceptanceState === "rejected" ? "scale(1)" : "scale(1.01)",
          boxShadow:
            acceptanceState === "accepted"
              ? "0 0 25px rgba(76, 175, 80, 0.4)"
              : "0 0 10px rgba(209, 117, 182, 0.2)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.9rem",
            color: source === "provided" ? "#fff" : "#D175B6",
            fontWeight: "600",
          }}
        >
          {title}
        </Typography>
        {hasData && (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Button
              onClick={onAccept}
              startIcon={
                acceptanceState === "accepted" ? (
                  <CheckCircleIcon sx={{ fontSize: "1rem" }} />
                ) : null
              }
              sx={{
                fontSize: "0.75rem",
                padding: "4px 10px",
                minWidth: "auto",
                backgroundColor:
                  acceptanceState === "accepted"
                    ? "rgba(76, 175, 80, 0.15)"
                    : "transparent",
                color: acceptanceState === "accepted" ? "#4caf50" : "#888",
                textTransform: "none",
                fontWeight: acceptanceState === "accepted" ? "600" : "500",
                border: `1px solid ${acceptanceState === "accepted" ? "#4caf50" : "#444"}`,
                borderRadius: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.2)",
                  color: "#4caf50",
                  borderColor: "#4caf50",
                },
              }}
            >
              {acceptanceState === "accepted" ? "Added" : "Add"}
            </Button>
            <Button
              onClick={onReject}
              startIcon={
                acceptanceState === "rejected" ? (
                  <CancelIcon sx={{ fontSize: "1rem" }} />
                ) : null
              }
              sx={{
                fontSize: "0.75rem",
                padding: "4px 10px",
                minWidth: "auto",
                backgroundColor:
                  acceptanceState === "rejected"
                    ? "rgba(244, 67, 54, 0.15)"
                    : "transparent",
                color: acceptanceState === "rejected" ? "#f44336" : "#888",
                textTransform: "none",
                fontWeight: acceptanceState === "rejected" ? "600" : "500",
                border: `1px solid ${acceptanceState === "rejected" ? "#f44336" : "#444"}`,
                borderRadius: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.2)",
                  color: "#f44336",
                  borderColor: "#f44336",
                },
              }}
            >
              {acceptanceState === "rejected" ? "Rejected" : "Reject"}
            </Button>
          </Box>
        )}
      </Box>
      {data}
    </Box>
  );
};

DataSection.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.node.isRequired,
  source: PropTypes.string.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  acceptanceState: PropTypes.string,
  isEmailSection: PropTypes.bool,
  hasData: PropTypes.bool,
};

export default function MergeClientBilling({
  open,
  onClose,
  providedData,
  companyData,
  formik,
  onSaveClick,
  preSelectedCompany = null,
}) {
  const isSmallScreen = useMediaQuery((theme) =>
    theme?.breakpoints?.down("md"),
  );

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [mergedData, setMergedData] = useState({
    companyName: "",
    companyAddress: "",
    isSameAddress: false,
    COIAddress: "",
    emails: {
      estimateEmails: [],
      depositInvoiceEmails: [],
      invoiceEmails: [],
    },
  });

  const [acceptanceState, setAcceptanceState] = useState({
    companyAddress: null,
    isSameAddress: null,
    COIAddress: null,
    provided_companyAddress: null,
    provided_isSameAddress: null,
    provided_COIAddress: null,
    estimateEmails: null,
    depositInvoiceEmails: null,
    invoiceEmails: null,
    provided_estimateEmails: null,
    provided_depositInvoiceEmails: null,
    provided_invoiceEmails: null,
  });

  const [selectedEmails, setSelectedEmails] = useState({
    estimateEmails: {},
    depositInvoiceEmails: {},
    invoiceEmails: {},
  });

  const [showEmailDetailModal, setShowEmailDetailModal] = useState(false);

  useEffect(() => {
    if (open) {
      setMergedData({
        companyName: formik.values?.companyName || "",
        companyAddress: formik.values?.companyAddress || "",
        isSameAddress: formik.values?.isSameAddress || false,
        COIAddress: formik.values?.COIAddress || "",
        emails: {
          estimateEmails: formik.values?.emails?.estimateEmails || [],
          depositInvoiceEmails:
            formik.values?.emails?.depositInvoiceEmails || [],
          invoiceEmails: formik.values?.emails?.invoiceEmails || [],
        },
      });
      setSelectedCompany(preSelectedCompany);
      setSelectedEmails({
        estimateEmails: {},
        depositInvoiceEmails: {},
        invoiceEmails: {},
      });
      setAcceptanceState({
        companyAddress: null,
        isSameAddress: null,
        COIAddress: null,
        provided_companyAddress: null,
        provided_isSameAddress: null,
        provided_COIAddress: null,
        estimateEmails: null,
        depositInvoiceEmails: null,
        invoiceEmails: null,
        provided_estimateEmails: null,
        provided_depositInvoiceEmails: null,
        provided_invoiceEmails: null,
      });
    }
  }, [open]);

  function handleClose() {
    onClose();
  }

  const getEmailKey = (email, type) => {
    return `${email?.name}_${email?.[`${type}Email`]}`;
  };

  const handleEmailToggle = (emailType, email, source) => {
    const key = `${emailType}Emails`;
    const emailKey = getEmailKey(email, emailType);
    const sourceKey = `${source}_${emailKey}`;

    setSelectedEmails((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [sourceKey]: !prev[key]?.[sourceKey],
      },
    }));
  };

  const handleSave = () => {
    const finalData = { ...mergedData };

    // Process non-email fields
    Object.keys(acceptanceState).forEach((key) => {
      const state = acceptanceState[key];
      const isProvided = key.startsWith("provided_");
      const actualKey = isProvided ? key.replace("provided_", "") : key;

      if (state === "accepted" && !actualKey.includes("Emails")) {
        if (
          actualKey === "companyAddress" ||
          actualKey === "isSameAddress" ||
          actualKey === "COIAddress"
        ) {
          if (isProvided) {
            finalData[actualKey] = providedData?.[actualKey];
          } else {
            finalData[actualKey] =
              selectedCompany?.clientDetails?.[actualKey] ??
              selectedCompany?.[actualKey];
          }
        }
      }
    });

    // Process email fields separately - check individual selections
    ["estimateEmails", "depositInvoiceEmails", "invoiceEmails"].forEach(
      (emailKey) => {
        const emailType = emailKey.replace("Emails", "");
        const providedEmails = providedData?.emails?.[emailKey] || [];
        const companyEmails =
          selectedCompany?.clientDetails?.emails?.[emailKey] || [];

        const selectedProvidedEmails = providedEmails.filter((email) => {
          const key = getEmailKey(email, emailType);
          return selectedEmails[emailKey]?.[`provided_${key}`] === true;
        });

        const selectedCompanyEmails = companyEmails.filter((email) => {
          const key = getEmailKey(email, emailType);
          return selectedEmails[emailKey]?.[`company_${key}`] === true;
        });

        finalData.emails[emailKey] = [
          ...selectedProvidedEmails,
          ...selectedCompanyEmails,
        ];
      },
    );

    Object.keys(finalData).forEach((key) => {
      formik.setFieldValue(key, finalData[key]);
    });

    onSaveClick();
    handleClose();
  };

  const handleAcceptReject = (field, action, source = "company") => {
    const stateKey = source === "provided" ? `provided_${field}` : field;

    if (field.includes("Emails")) {
      const emailType = field.replace("Emails", "");
      const emails =
        source === "provided"
          ? providedData?.emails?.[field] || []
          : selectedCompany?.clientDetails?.emails?.[field] || [];

      if (action === "accept") {
        const updatedSelections = {};
        emails.forEach((email) => {
          const emailKey = getEmailKey(email, emailType);
          updatedSelections[`${source}_${emailKey}`] = true;
        });

        setSelectedEmails((prev) => ({
          ...prev,
          [field]: {
            ...prev[field],
            ...updatedSelections,
          },
        }));
      } else {
        const updatedSelections = {};
        emails.forEach((email) => {
          const emailKey = getEmailKey(email, emailType);
          updatedSelections[`${source}_${emailKey}`] = false;
        });

        setSelectedEmails((prev) => ({
          ...prev,
          [field]: {
            ...prev[field],
            ...updatedSelections,
          },
        }));
      }

      setAcceptanceState((prev) => ({
        ...prev,
        [stateKey]: action === "accept" ? "accepted" : "rejected",
      }));
    } else {
      if (action === "accept") {
        const oppositeKey = source === "provided" ? field : `provided_${field}`;
        setAcceptanceState((prev) => ({
          ...prev,
          [stateKey]: "accepted",
          [oppositeKey]: "rejected",
        }));
      } else {
        setAcceptanceState((prev) => ({
          ...prev,
          [stateKey]: "rejected",
        }));
      }
    }
  };

  const hasEmailDifference = (emailType) => {
    const key = `${emailType}Emails`;
    const providedEmails = providedData?.emails?.[key] || [];
    const companyEmails = selectedCompany?.clientDetails?.emails?.[key] || [];

    if (providedEmails.length !== companyEmails.length) return true;

    return providedEmails.some(
      (pEmail) =>
        !companyEmails.some(
          (cEmail) =>
            cEmail?.[`${emailType}Email`] === pEmail?.[`${emailType}Email`] &&
            cEmail?.name === pEmail?.name,
        ),
    );
  };

  const handleEmailDetailSave = (selectedEmails) => {
    setMergedData((prev) => ({
      ...prev,
      emails: selectedEmails,
    }));

    Object.keys(selectedEmails).forEach((key) => {
      const emailType = key.replace("Emails", "");
      const selectedEmailList = selectedEmails[key];
      const providedEmails = providedData?.emails?.[key] || [];
      const companyEmails = selectedCompany?.clientDetails?.emails?.[key] || [];

      const hasProvidedEmails = providedEmails.some((pEmail) =>
        selectedEmailList.some(
          (sEmail) =>
            sEmail?.[`${emailType}Email`] === pEmail?.[`${emailType}Email`] &&
            sEmail?.name === pEmail?.name,
        ),
      );

      const hasCompanyEmails = companyEmails.some((cEmail) =>
        selectedEmailList.some(
          (sEmail) =>
            sEmail?.[`${emailType}Email`] === cEmail?.[`${emailType}Email`] &&
            sEmail?.name === cEmail?.name,
        ),
      );

      setAcceptanceState((prev) => ({
        ...prev,
        [`provided_${key}`]: hasProvidedEmails ? "accepted" : "rejected",
        [key]: hasCompanyEmails ? "accepted" : "rejected",
      }));
    });
  };

  const renderFieldComparison = (
    fieldName,
    label,
    providedValue,
    companyValue,
  ) => {
    if (providedValue === companyValue) return null;

    const hasProvidedData =
      providedValue !== null &&
      providedValue !== undefined &&
      providedValue !== "";
    const hasCompanyData =
      companyValue !== null &&
      companyValue !== undefined &&
      companyValue !== "";

    return (
      <Grid item xs={12}>
        <Typography
          sx={{
            ...titleSx,
            mb: 1,
            fontSize: "0.9rem",
          }}
        >
          {label}:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <DataSection
              title="Provided Data"
              source="provided"
              acceptanceState={acceptanceState[`provided_${fieldName}`]}
              onAccept={() =>
                handleAcceptReject(fieldName, "accept", "provided")
              }
              onReject={() =>
                handleAcceptReject(fieldName, "reject", "provided")
              }
              hasData={hasProvidedData}
              data={
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: "0.85rem",
                    wordBreak: "break-word",
                  }}
                >
                  {typeof providedValue === "boolean"
                    ? providedValue
                      ? "Yes"
                      : "No"
                    : providedValue || "N/A"}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DataSection
              title="Company Data"
              source="company"
              acceptanceState={acceptanceState[fieldName]}
              onAccept={() =>
                handleAcceptReject(fieldName, "accept", "company")
              }
              onReject={() =>
                handleAcceptReject(fieldName, "reject", "company")
              }
              hasData={hasCompanyData}
              data={
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: "0.85rem",
                    wordBreak: "break-word",
                  }}
                >
                  {typeof companyValue === "boolean"
                    ? companyValue
                      ? "Yes"
                      : "No"
                    : companyValue || "N/A"}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderEmailComparison = (emailType) => {
    const key = `${emailType}Emails`;
    const providedEmails = providedData?.emails?.[key] || [];
    const companyEmails = selectedCompany?.clientDetails?.emails?.[key] || [];

    const hasDifference = hasEmailDifference(emailType);
    if (!hasDifference) return null;

    const hasProvidedData = providedEmails.length > 0;
    const hasCompanyData = companyEmails.length > 0;

    const renderEmailChips = (emails, type, source) => {
      if (!emails || emails.length === 0) {
        return (
          <Typography sx={{ color: "#888", fontSize: "0.85rem" }}>
            No profiles added
          </Typography>
        );
      }

      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {emails.map((email, index) => {
            const emailKey = getEmailKey(email, type);
            const sourceKey = `${source}_${emailKey}`;
            const isSelected = selectedEmails[key]?.[sourceKey];

            return (
              <CustomTooltip
                key={index}
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      gap: 0.5,
                      backgroundColor: "rgba(209, 117, 182, 0.1)",
                      borderRadius: "16px",
                      padding: "4px 8px 4px 12px",
                      // border: `1px solid ${isSelected === true ? getCssVariableHandler({ variableName: "--primary-pink-hex" }) : isSelected === false ? "#f44336" : "rgba(209, 117, 182, 0.3)"}`,
                      // opacity: isSelected === false ? 0.3 : 1,
                      transform:
                        isSelected === true ? "scale(1.05)" : "scale(1.02)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow:
                        isSelected === true
                          ? `0 0 15px ${getCssVariableHandler({ variableName: "--primary-pink-hex" })}40`
                          : isSelected === false
                            ? "0 0 10px rgba(244, 67, 54, 0.3)"
                            : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                      <strong style={{ color: "#D175B6" }}>Name:</strong>{" "}
                      {email?.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                      <strong style={{ color: "#D175B6" }}>Email:</strong>{" "}
                      {email?.[`${type}Email`]}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      <strong style={{ color: "#D175B6" }}>Mobile:</strong>{" "}
                      {phoneNumberFormat(email?.[`${type}Mobile`])}
                    </Typography>
                  </Box>
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: isSelected
                      ? alpha(
                          getCssVariableHandler({
                            variableName: "--primary-pink-hex",
                          }),
                          0.5,
                        )
                      : "rgba(209, 117, 182, 0.1)",
                    borderRadius: "16px",
                    padding: "4px 8px 4px 12px",
                    border: "1px solid rgba(209, 117, 182, 0.3)",
                    opacity: isSelected === false ? 0.3 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: "#fff",
                    }}
                  >
                    {email?.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.25, ml: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEmailToggle(emailType, email, source);
                      }}
                      sx={{
                        padding: "2px",
                        color:
                          isSelected === true
                            ? getCssVariableHandler({
                                variableName: "--primary-pink-hex",
                              })
                            : "#888",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: getCssVariableHandler({
                            variableName: "--primary-pink-hex",
                          }),
                          transform: "scale(1.2) rotate(10deg)",
                        },
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEmails((prev) => ({
                          ...prev,
                          [key]: {
                            ...prev[key],
                            [sourceKey]: false,
                          },
                        }));
                      }}
                      sx={{
                        padding: "2px",
                        color: isSelected === false ? "#f44336" : "#888",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: "#f44336",
                          transform: "scale(1.2) rotate(-10deg)",
                        },
                      }}
                    >
                      <CancelIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Box>
                </Box>
              </CustomTooltip>
            );
          })}
        </Box>
      );
    };

    return (
      <Grid item xs={12}>
        <Typography
          sx={{
            ...titleSx,
            mb: 1,
            fontSize: "0.9rem",
          }}
        >
          {CLIENT_EMAIL_LABEL?.[emailType]} Profiles:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <DataSection
              title="Provided Data"
              source="provided"
              acceptanceState={acceptanceState[`provided_${key}`]}
              onAccept={() => handleAcceptReject(key, "accept", "provided")}
              onReject={() => handleAcceptReject(key, "reject", "provided")}
              isEmailSection={true}
              hasData={hasProvidedData}
              data={renderEmailChips(providedEmails, emailType, "provided")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DataSection
              title="Company Data"
              source="company"
              acceptanceState={acceptanceState[key]}
              onAccept={() => handleAcceptReject(key, "accept", "company")}
              onReject={() => handleAcceptReject(key, "reject", "company")}
              isEmailSection={true}
              hasData={hasCompanyData}
              data={renderEmailChips(companyEmails, emailType, "company")}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const hasAnyEmailDifference =
    selectedCompany &&
    (hasEmailDifference(CLIENT_EMAIL.estimate) ||
      hasEmailDifference(CLIENT_EMAIL.depositInvoice) ||
      hasEmailDifference(CLIENT_EMAIL.invoice));

  const hasCompanyDetailsDifference = () => {
    if (!selectedCompany) return false;

    const addressDiff =
      providedData?.companyAddress !==
      selectedCompany?.clientDetails?.companyAddress;
    const sameAddressDiff =
      providedData?.isSameAddress !== selectedCompany?.isSameAddress;
    const coiDiff =
      providedData?.COIAddress !== selectedCompany?.clientDetails?.COIAddress;

    return addressDiff || sameAddressDiff || coiDiff;
  };

  const hasDifferences =
    selectedCompany &&
    (providedData?.companyAddress !==
      selectedCompany?.clientDetails?.companyAddress ||
      providedData?.isSameAddress !== selectedCompany?.isSameAddress ||
      providedData?.COIAddress !== selectedCompany?.clientDetails?.COIAddress ||
      hasAnyEmailDifference);

  return (
    <Modal
      className="modelBox"
      open={open}
      onClose={handleClose}
      aria-labelledby="merge-client-billing"
      aria-describedby="merge-client-description"
    >
      <Box sx={style} className="custom-scroll-design">
        <Typography
          className="model-title"
          id="modal-modal-title"
          variant="h6"
          component="h2"
          marginBottom="20px"
        >
          Select From Existing Client
        </Typography>

        <Typography
          sx={{
            mb: 2,
            fontSize: "0.85rem",
            color: "#888",
          }}
        >
          Search and select a company to compare billing information
        </Typography>

        {/* <Box sx={{ mb: 3 }}> */}
        {/*   <CompaniesAutocomplete */}
        {/*     placeholder="Search Companies" */}
        {/*     noOptionsText="No Companies Found" */}
        {/*     value={selectedCompany} */}
        {/*     name="companySearch" */}
        {/*     fetchOptions */}
        {/*     getOptionLabel={(op) => op?.companyName} */}
        {/*     onChange={(_, newVal) => { */}
        {/*       setSelectedCompany(newVal); */}
        {/*       if (newVal) { */}
        {/*         setMergedData((prev) => ({ */}
        {/*           ...prev, */}
        {/*           companyName: newVal?.companyName, */}
        {/*         })); */}
        {/*       } */}
        {/*     }} */}
        {/*   /> */}
        {/* </Box> */}
        <Box sx={{ mb: 3 }}>
          <CompaniesAutocomplete
            placeholder="Search Companies"
            noOptionsText="No Companies Found"
            value={selectedCompany}
            name="companySearch"
            fetchOptions
            getOptionLabel={(op) => op?.companyName || ""}
            isOptionEqualToValue={(option, value) =>
              option?.companyName === value?.companyName
            }
            onChange={(_, newVal) => {
              const newCompanyVal = {
                ...newVal,
                clientDetails: {
                  ...newVal?.clientDetails,
                  emails: {
                    depositInvoiceEmails:
                      newVal?.clientDetails?.emails?.depositInvoiceEmails?.map(
                        (e) => ({
                          ...e,
                          depositInvoiceMobile: phoneNumberFormat(
                            e?.depositInvoiceMobile,
                          ),
                        }),
                      ),
                    estimateEmails:
                      newVal?.clientDetails?.emails?.estimateEmails?.map(
                        (e) => ({
                          ...e,
                          estimateMobile: phoneNumberFormat(e?.estimateMobile),
                        }),
                      ),
                    invoiceEmails:
                      newVal?.clientDetails?.emails?.invoiceEmails?.map(
                        (e) => ({
                          ...e,
                          invoiceMobile: phoneNumberFormat(e?.invoiceMobile),
                        }),
                      ),
                  },
                },
              };
              setSelectedCompany(newCompanyVal);
              // if (newVal) {
              //   setMergedData((prev) => ({
              //     ...prev,
              //     companyName: newVal?.companyName,
              //   }));
              // }
            }}
          />
          {/* {preSelectedCompany && ( */}
          {/*   <Typography sx={{ fontSize: "0.75rem", color: "#888", mt: 0.5 }}> */}
          {/*     {preSelectedCompany?.companyName} vs Contact Information Data */}
          {/*   </Typography> */}
          {/* )} */}
        </Box>

        {selectedCompany && !hasDifferences && (
          <Box
            sx={{
              border: "1px solid #4caf50",
              borderRadius: "0.5rem",
              p: 2,
              mb: 2,
              backgroundColor: "rgba(76, 175, 80, 0.1)",
            }}
          >
            <Typography sx={{ color: "#4caf50", textAlign: "center" }}>
              No differences found between provided data and selected company
            </Typography>
          </Box>
        )}

        {selectedCompany && hasDifferences && (
          <>
            <Typography
              sx={{
                mb: 2,
                fontSize: "0.85rem",
                color: "#888",
              }}
            >
              The following fields differ from your provided billing
              information. Choose which values you want to add to your client:
            </Typography>

            {hasCompanyDetailsDifference() && (
              <Box
                sx={{
                  border: `1px solid ${getCssVariableHandler({
                    variableName: "--primary-pink-hex",
                  })}`,
                  borderRadius: "0.5rem",
                  p: 2,
                  mb: 2,
                  animation: `${slideIn} 0.5s ease-out`,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography sx={{ ...titleSx }}>
                      Company Details:
                    </Typography>
                  </Grid>

                  {renderFieldComparison(
                    "companyAddress",
                    "Company Address",
                    providedData?.companyAddress,
                    selectedCompany?.clientDetails?.companyAddress,
                  )}
                  {renderFieldComparison(
                    "isSameAddress",
                    "Use Same Address for COI",
                    providedData?.isSameAddress,
                    selectedCompany?.isSameAddress,
                  )}

                  {!mergedData.isSameAddress &&
                    renderFieldComparison(
                      "COIAddress",
                      "COI Address",
                      providedData?.COIAddress,
                      selectedCompany?.clientDetails?.COIAddress,
                    )}
                </Grid>
              </Box>
            )}

            {hasAnyEmailDifference && (
              <Box
                sx={{
                  border: `1px solid ${getCssVariableHandler({
                    variableName: "--primary-pink-hex",
                  })}`,
                  borderRadius: "0.5rem",
                  p: 2,
                  mb: 2,
                  animation: `${slideIn} 0.5s ease-out`,
                }}
              >
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ ...titleSx }}>
                      Billing Information:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#888",
                        fontStyle: "italic",
                      }}
                    >
                      Click check/cross icons to select individual emails
                    </Typography>
                  </Grid>

                  {renderEmailComparison(CLIENT_EMAIL.estimate)}
                  {renderEmailComparison(CLIENT_EMAIL.depositInvoice)}
                  {renderEmailComparison(CLIENT_EMAIL.invoice)}
                </Grid>
              </Box>
            )}
          </>
        )}

        <Box className="bottom-btn-box">
          <Button className="btn-black" onClick={handleClose}>
            <Typography className="btn-text">Close</Typography>
          </Button>
          <Button
            className="btn-pink"
            onClick={handleSave}
            disabled={!selectedCompany}
          >
            <Typography className="btn-text">Merge</Typography>
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

MergeClientBilling.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  providedData: PropTypes.object,
  companyData: PropTypes.object,
  formik: PropTypes.object.isRequired,
  onSaveClick: PropTypes.func,
  preSelectedCompany: PropTypes.object,
};